import { useState } from 'react'
import { Loader2, Plus, Trash2 } from 'lucide-react'
import { DrawerSection } from '@/shared/ui/Drawer'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { createMark, deleteMark, listEventMarks } from '../api'
import { fmtClock } from '../players'
import { MARK_KIND_ORDER, markKindIcon, markKindLabel } from '../vocab'
import { explain } from '@/shared/api/problem'
import type { CoverageEventResponse } from '@/shared/api/types'

/**
 * Timestamped moments.
 *
 * A two-hour tally-centre watch has four minutes in it that matter. Nobody
 * scrubs two hours to find them, so in practice nobody watches it, so in
 * practice the archive is write-only. A mark is a timestamp and a sentence,
 * dropped while it is happening.
 */
export function MarksPanel({ event }: { event: CoverageEventResponse }) {
  const canMark = useCan('coverage:mark')
  const [tick, setTick] = useState(0)
  const [label, setLabel] = useState('')
  const [kindCode, setKindCode] = useState<string>('MOMENT')
  const [feedId, setFeedId] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data, error: loadError, loading } = useLoad(
    () => listEventMarks(event.id), [event.id, tick],
  )
  const marks = data ?? []

  return (
    <div className="space-y-3">
      <ErrorStrip message={loadError ?? error} />

      {canMark && (
        <DrawerSection title="Mark a moment">
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              <input
                className="erp-input h-9 min-w-[12rem] flex-1"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="What just happened"
                aria-label="What just happened"
                onKeyDown={(e) => { if (e.key === 'Enter' && label.trim()) void submit() }}
              />
              <select className="erp-input h-9" value={kindCode} aria-label="Kind"
                onChange={(e) => setKindCode(e.target.value)}>
                {MARK_KIND_ORDER.map((k) => (
                  <option key={k} value={k}>{markKindLabel(k)}</option>
                ))}
              </select>
              {event.feeds.length > 0 && (
                <select className="erp-input h-9" value={feedId} aria-label="Camera"
                  onChange={(e) => setFeedId(e.target.value)}>
                  <option value="">Any camera</option>
                  {event.feeds.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
                </select>
              )}
              <button
                type="button"
                className="erp-btn erp-btn-primary h-9"
                disabled={!label.trim() || busy}
                onClick={() => void submit()}
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                Mark
              </button>
            </div>
            <Note tone="info">
              The time is taken now, on the server. It cannot be edited afterwards — a mark
              whose timestamp can be moved is not a record of when something happened. Wrong
              time means delete and mark again.
            </Note>
          </div>
        </DrawerSection>
      )}

      {/* No count while it is still loading: "Moments (0)" over a spinner is a
          claim about data nobody has yet. */}
      <DrawerSection title={loading && marks.length === 0 ? 'Moments' : `Moments (${marks.length})`}>
        {loading && marks.length === 0 ? <LoadingBlock label="Loading moments…" />
          : marks.length === 0 ? (
            <Note tone="info">
              Nothing marked. Marks that fall inside a recording become chapters on its
              scrub bar.
            </Note>
          ) : (
            <ul className="space-y-1">
              {marks.map((m) => (
                <li key={m.id} className="flex items-start gap-2 rounded-md border border-border p-2">
                  <span className="mt-0.5 shrink-0 text-muted-foreground">
                    {markKindIcon(m.kindCode)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-erp-sm font-medium">{m.label}</div>
                    <div className="truncate text-erp-xs text-muted-foreground">
                      {new Date(m.occurredAt).toLocaleTimeString()}
                      {' · '}
                      {/* Null offset is the normal case for a mark dropped while
                          no camera was rolling. It is not missing data. */}
                      {m.offsetSec != null
                        ? <span className="font-mono">{fmtClock(m.offsetSec)} into the recording</span>
                        : <span>outside every recording</span>}
                      {m.authorName && ` · ${m.authorName}`}
                    </div>
                    {m.note && <p className="mt-0.5 text-erp-xs text-muted-foreground">{m.note}</p>}
                  </div>
                  {canMark && (
                    <button
                      type="button"
                      className="erp-btn h-7 shrink-0"
                      aria-label={`Delete mark ${m.label}`}
                      onClick={async () => {
                        setError(null)
                        try { await deleteMark(m.id); setTick((t) => t + 1) }
                        catch (e) { setError(explain(e)) }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
      </DrawerSection>
    </div>
  )

  async function submit() {
    setBusy(true); setError(null)
    try {
      await createMark(event.id, {
        label: label.trim(),
        kindCode,
        feedId: feedId || null,
        // occurredAt is deliberately NOT sent. The server stamps it, and it will
        // refuse anything more than five minutes old or in the future anyway.
      })
      setLabel('')
      setTick((t) => t + 1)
    } catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }
}
